import type { OnlineOrder, PublicInvoice } from '@arabat/contracts';
import { Money } from '@arabat/money';
import type { InvoiceData } from './invoice-html.js';

/*
 * The A4 invoice data of an online order (owner, 2026-10-25), shared by the console (print / PDF)
 * and the public invoice page linked from the store's WhatsApp message (ADR-0138 amendment 6).
 */

/** The store fields the invoice header shows. */
export type InvoiceStore = {
  nameAr: string | null;
  nameEn: string | null;
  logoUrl: string | null;
  primaryColor: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
};

const minor = (m: { amount: string }) => Money.parse(m.amount, 'KWD').minor;

/** "25/10/2026 10:34": plain digits, so the date never reorders inside Arabic text. */
function stamp(iso: string, timeZone?: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...(timeZone ? { timeZone } : {}),
  })
    .format(new Date(iso))
    .replace(',', '');
}

/** Arabic first (the shop's language), English when there is no Arabic name. */
const name = (item: { nameAr: string | null; nameEn: string | null }) =>
  item.nameAr ?? item.nameEn ?? '';

/** `timeZone`: the merchant's (server side); the browser's own zone when left out. */
export function onlineInvoiceData(
  order: OnlineOrder,
  store: InvoiceStore | undefined,
  timeZone?: string,
): InvoiceData {
  const voided = ['cancelled', 'expired', 'payment_failed'].includes(order.status);
  return {
    storeName: (store && name(store)) || name(order.branch),
    logoUrl: store?.logoUrl ?? null,
    accent: store?.primaryColor ?? null,
    storePhone: store?.contactPhone ?? null,
    storeEmail: store?.contactEmail ?? null,
    branchName: name(order.branch) || null,
    branchAddress: order.branch.address,
    branchPhone: order.branch.phone,
    number: order.number,
    receiptNumber: order.receiptNumber,
    issuedAt: stamp(order.createdAt, timeZone),
    state: voided ? 'cancelled' : order.paidAt || order.saleId ? 'paid' : 'unpaid',
    paidAt: order.paidAt ? stamp(order.paidAt, timeZone) : null,
    paymentMethod: order.paymentMethod
      ? name(order.paymentMethod)
      : order.paymentMode === 'on_receipt'
        ? 'الدفع عند الاستلام / Pay on receipt'
        : null,
    paymentReference: order.paymentReference,
    fulfillment: order.fulfillment,
    wanted: order.scheduledFor
      ? stamp(order.scheduledFor, timeZone)
      : 'في أقرب وقت / As soon as possible',
    customerName: order.customerName,
    customerPhone: order.customerPhone,
    customerEmail: order.customerEmail,
    // The governorate area the shopper picked (ADR-0140), else the zone's name.
    deliveryArea: order.deliveryPlace
      ? `${name(order.deliveryPlace)}، ${name(order.deliveryPlace.region)}`
      : (order.deliveryArea && name(order.deliveryArea)) || order.deliveryAreaName,
    deliveryAddress: order.deliveryAddress,
    note: order.note,
    lines: order.lines.map((l) => ({
      name: name(l),
      detail:
        [name({ nameAr: l.variantNameAr, nameEn: l.variantNameEn }), l.sku]
          .filter(Boolean)
          .join(' · ') || null,
      // The shopper's choices and note (ADR-0160).
      options: (l.options ?? []).map((o) => {
        const group = name({ nameAr: o.groupNameAr, nameEn: o.groupNameEn });
        const choice = name(o);
        return group ? `${group}: ${choice}` : choice;
      }),
      note: l.note ?? null,
      quantity: l.quantity.replace(/\.000$/, ''),
      unitPriceMinor: minor(l.unitPrice),
      totalMinor: minor(l.lineTotal),
    })),
    subtotalMinor: minor(order.subtotal),
    discountMinor: minor(order.discount),
    promotion: order.promotion
      ? [order.promotion.name, order.promotion.code].filter(Boolean).join(' · ')
      : null,
    deliveryFeeMinor: minor(order.deliveryFee),
    totalMinor: minor(order.total),
  };
}

/** The invoice for the public API: bigint amounts as decimal strings. */
export function invoiceToJson(data: InvoiceData): PublicInvoice {
  return {
    ...data,
    lines: data.lines.map((l) => ({
      ...l,
      options: l.options ?? [],
      note: l.note ?? null,
      unitPriceMinor: l.unitPriceMinor.toString(),
      totalMinor: l.totalMinor.toString(),
    })),
    subtotalMinor: data.subtotalMinor.toString(),
    discountMinor: data.discountMinor.toString(),
    deliveryFeeMinor: data.deliveryFeeMinor.toString(),
    totalMinor: data.totalMinor.toString(),
  };
}

export function invoiceFromJson(json: PublicInvoice): InvoiceData {
  return {
    ...json,
    lines: json.lines.map((l) => ({
      ...l,
      unitPriceMinor: BigInt(l.unitPriceMinor),
      totalMinor: BigInt(l.totalMinor),
    })),
    subtotalMinor: BigInt(json.subtotalMinor),
    discountMinor: BigInt(json.discountMinor),
    deliveryFeeMinor: BigInt(json.deliveryFeeMinor),
    totalMinor: BigInt(json.totalMinor),
  };
}

/**
 * The A4 invoice made readable on a screen (ADR-0138 amendment 7): below 700 px the sheet fits
 * the width; the 794 px print / PDF frame and printing keep A4. Added after the invoice's own
 * rules so it wins.
 */
export function invoiceForScreen(html: string): string {
  return html.replace(
    '</head>',
    '<style>@media screen and (max-width: 700px) { .page { width: 100%; min-height: 0; padding: 5mm; gap: 5mm; }' +
      ' .boxes { grid-template-columns: 1fr; } table { font-size: 11px; } }</style></head>',
  );
}
